import React, { useState, useMemo } from 'react';
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  DropdownDivider,
} from '../common/Dropdown';
import type {
  AdditionalActivityStat,
  AdditionalActivitiesManagementLabels,
} from '../../shared/types/additional-activities';

export interface AdditionalActivitiesManagementProps {
  activities: AdditionalActivityStat[];
  backUrl: string;
  convertToToggleFieldBaseUrl: string;
  sortBy: string;
  sortDirection: string;
  onSort: (field: string) => void;
  onRename: (oldName: string, newName: string) => Promise<any>;
  onMerge: (sourceNames: string[], targetName: string) => Promise<any>;
  onDelete: (names: string[]) => Promise<any>;
  labels: AdditionalActivitiesManagementLabels;
}

export function AdditionalActivitiesManagement({
  activities,
  backUrl,
  convertToToggleFieldBaseUrl,
  sortBy,
  sortDirection,
  onSort,
  onRename,
  onMerge,
  onDelete,
  labels,
}: AdditionalActivitiesManagementProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [renameModal, setRenameModal] = useState<{ show: boolean; activityName: string }>({
    show: false,
    activityName: '',
  });
  const [mergeModal, setMergeModal] = useState<{ show: boolean; activityName: string }>({
    show: false,
    activityName: '',
  });
  const [newName, setNewName] = useState('');
  const [mergeTarget, setMergeTarget] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Filter activities by search term
  const filteredActivities = useMemo(() => {
    if (!searchTerm.trim()) return activities;
    return activities.filter((a) =>
      a.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [activities, searchTerm]);

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return null;
    return sortDirection === 'asc' ? 'bi-sort-alpha-down' : 'bi-sort-alpha-down-alt';
  };

  const getUsageSortIcon = () => {
    if (sortBy !== 'usage_count') return null;
    return sortDirection === 'asc' ? 'bi-sort-numeric-down' : 'bi-sort-numeric-down-alt';
  };

  const handleRenameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;

    setIsSubmitting(true);
    try {
      await onRename(renameModal.activityName, newName.trim());
      setRenameModal({ show: false, activityName: '' });
      setNewName('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMergeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mergeTarget) return;

    setIsSubmitting(true);
    try {
      await onMerge([mergeModal.activityName, mergeTarget], mergeTarget);
      setMergeModal({ show: false, activityName: '' });
      setMergeTarget('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (activityName: string) => {
    if (!confirm(labels.confirmDelete.replace('%{name}', activityName))) return;

    await onDelete([activityName]);
  };

  const getConvertUrl = (activityName: string) => {
    return convertToToggleFieldBaseUrl.replace('__ACTIVITY_NAME__', encodeURIComponent(activityName));
  };

  if (activities.length === 0) {
    return (
      <div className="container-fluid py-4">
        <Breadcrumbs title={labels.title} />
        <Header title={labels.title} subtitle={labels.subtitle} />

        <div className="row">
          <div className="col-lg-8 offset-lg-2">
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center py-5">
                <i className="bi bi-tags display-1 text-muted mb-3"></i>
                <h3 className="mb-3">{labels.noActivities}</h3>
                <p className="text-muted mb-0">{labels.noActivitiesDescription}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <Breadcrumbs title={labels.title} />
      <Header title={labels.title} subtitle={labels.subtitle} />

      <div className="row">
        <div className="col">
          <div className="card border-0 shadow-sm">
            {/* Search header */}
            <div className="card-header bg-white border-bottom">
              <div className="row align-items-center g-3">
                <div className="col-lg-12">
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <i className="bi bi-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 ps-0"
                      placeholder={`${labels.searchActivities}...`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className={activities.length > 5 ? 'table-responsive' : ''}>
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="border-bottom">
                      <button
                        type="button"
                        className="btn btn-link text-dark text-decoration-none p-0 d-flex align-items-center"
                        onClick={() => onSort('name')}
                      >
                        <span>{labels.name}</span>
                        {getSortIcon('name') && (
                          <i className={`bi ${getSortIcon('name')} ms-1`}></i>
                        )}
                      </button>
                    </th>
                    <th className="border-bottom text-center" style={{ width: 80 }}>
                      <button
                        type="button"
                        className="btn btn-link text-dark text-decoration-none p-0 d-flex align-items-center justify-content-center"
                        onClick={() => onSort('usage_count')}
                      >
                        <span>{labels.uses}</span>
                        {getUsageSortIcon() && (
                          <i className={`bi ${getUsageSortIcon()} ms-1`}></i>
                        )}
                      </button>
                    </th>
                    <th className="text-end border-bottom" style={{ width: 60 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredActivities.map((activity) => (
                    <tr key={activity.name} className="border-bottom">
                      <td className="ps-3">
                        <div className="d-flex align-items-center">
                          <i className="bi bi-tag-fill text-primary me-2"></i>
                          <strong>{activity.name}</strong>
                        </div>
                      </td>
                      <td className="text-center">
                        <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary border border-primary">
                          {activity.usageCount}
                        </span>
                      </td>
                      <td className="text-end pe-3">
                        <Dropdown>
                          <DropdownTrigger className="btn btn-sm btn-outline-secondary dropdown-toggle p-1 px-2">
                            <i className="bi bi-three-dots-vertical"></i>
                          </DropdownTrigger>
                          <DropdownMenu align="end">
                            <DropdownItem
                              onClick={() => {
                                setRenameModal({ show: true, activityName: activity.name });
                                setNewName('');
                              }}
                            >
                              <i className="bi bi-pencil me-2"></i>
                              {labels.rename}
                            </DropdownItem>
                            <DropdownItem
                              onClick={() => {
                                setMergeModal({ show: true, activityName: activity.name });
                                setMergeTarget('');
                              }}
                            >
                              <i className="bi bi-union me-2"></i>
                              {labels.mergeInto}...
                            </DropdownItem>
                            <DropdownItem href={getConvertUrl(activity.name)}>
                              <i className="bi bi-arrow-repeat me-2"></i>
                              {labels.convertToToggleField}
                            </DropdownItem>
                            <DropdownDivider />
                            <DropdownItem
                              className="text-danger"
                              onClick={() => handleDelete(activity.name)}
                            >
                              <i className="bi bi-trash me-2"></i>
                              {labels.delete}
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="card-footer bg-light border-top">
              <div className="text-muted small">
                <i className="bi bi-info-circle me-1"></i>
                {labels.totalActivities.replace('%{count}', String(activities.length))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rename Modal */}
      {renameModal.show && (
        <Modal
          title={labels.renameActivity}
          onClose={() => setRenameModal({ show: false, activityName: '' })}
        >
          <form onSubmit={handleRenameSubmit}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  <i className="bi bi-tag me-1"></i>
                  {labels.currentName}:
                </label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={renameModal.activityName}
                  readOnly
                />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  <i className="bi bi-pencil me-1"></i>
                  {labels.newName}:
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={labels.enterNewActivityName}
                  required
                  autoFocus
                />
                <div className="form-text">
                  <i className="bi bi-info-circle me-1"></i>
                  {labels.renameDescription}
                </div>
              </div>
            </div>
            <div className="modal-footer bg-light border-top">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setRenameModal({ show: false, activityName: '' })}
                disabled={isSubmitting}
              >
                <i className="bi bi-x-circle me-1"></i>
                {labels.cancel}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting || !newName.trim()}
              >
                {isSubmitting ? (
                  <span className="spinner-border spinner-border-sm me-1" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </span>
                ) : (
                  <i className="bi bi-check-circle me-1"></i>
                )}
                {labels.renameActivity}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Merge Modal */}
      {mergeModal.show && (
        <Modal
          title={labels.mergeActivity}
          onClose={() => setMergeModal({ show: false, activityName: '' })}
        >
          <form onSubmit={handleMergeSubmit}>
            <div className="modal-body">
              <div className="alert alert-info d-flex align-items-start mb-4">
                <i className="bi bi-info-circle-fill me-2 mt-1"></i>
                <div>
                  <strong>{labels.aboutMerging}:</strong> {labels.mergeDescription}
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  <i className="bi bi-tag me-1"></i>
                  {labels.activityToMerge}:
                </label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={mergeModal.activityName}
                  readOnly
                />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">
                  <i className="bi bi-bullseye me-1"></i>
                  {labels.mergeInto}:
                </label>
                <select
                  className="form-select"
                  value={mergeTarget}
                  onChange={(e) => setMergeTarget(e.target.value)}
                  required
                >
                  <option value="">{labels.selectTargetActivity}</option>
                  {activities
                    .filter((a) => a.name !== mergeModal.activityName)
                    .map((a) => (
                      <option key={a.name} value={a.name}>
                        {a.name} ({a.usageCount} {labels.uses.toLowerCase()})
                      </option>
                    ))}
                </select>
                <div className="form-text">
                  <i className="bi bi-info-circle me-1"></i>
                  {labels.allOccurrencesWillBeMerged}
                </div>
              </div>
            </div>
            <div className="modal-footer bg-light border-top">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setMergeModal({ show: false, activityName: '' })}
                disabled={isSubmitting}
              >
                <i className="bi bi-x-circle me-1"></i>
                {labels.cancel}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting || !mergeTarget}
              >
                {isSubmitting ? (
                  <span className="spinner-border spinner-border-sm me-1" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </span>
                ) : (
                  <i className="bi bi-check-circle me-1"></i>
                )}
                {labels.mergeActivity}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

// Helper components
function Breadcrumbs({ title }: { title: string }) {
  return (
    <nav aria-label="breadcrumb" className="mb-3">
      <ol className="breadcrumb mb-0">
        <li className="breadcrumb-item active" aria-current="page">
          {title}
        </li>
      </ol>
    </nav>
  );
}

function Header({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="row mb-4">
      <div className="col">
        <h1 className="display-6 mb-2">
          <i className="bi bi-tags-fill text-primary me-2"></i>
          {title}
        </h1>
        <p className="text-muted mb-0">{subtitle}</p>
      </div>
    </div>
  );
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <>
      <div className="modal-backdrop fade show" onClick={onClose}></div>
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content shadow">
            <div className="modal-header bg-primary bg-opacity-10 border-bottom">
              <h5 className="modal-title d-flex align-items-center">
                <i className="bi bi-pencil-square me-2"></i>
                {title}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>
            {children}
          </div>
        </div>
      </div>
    </>
  );
}

export default AdditionalActivitiesManagement;
